// Office Hours — per-tenant business hours + Office Holidays.
// Layout matches 3CX (spec section "Office Hours per-Department page"):
//   • Left rail: tenant/department selector (alphabetical)
//   • Right pane:
//     - Tabs: Office Hours | Office Holidays
//     - Office Hours: timezone picker + 7-col day grid (multi-range) + Break grid
//     - Office Holidays: + Add toolbar + table (Name | Date | Time | Occurs | Prompt)
// Stored in stratus_ext.user_groups.office_hours JSONB. Schema:
//   { timezone, hours: { mon: [{start,end},...], ... }, breaks: { mon: [...] }, holidays: [...] }

import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getActiveTenant, ALL, matchesScope } from '../lib/tenantScope';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, X, Trash2, Edit2, Save, Upload, Mic, FolderOpen } from 'lucide-react';
import AudioPicker from '../components/AudioPicker';
import { getToken } from '../lib/api';

const auth = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });
const fJson = async (url: string, init: RequestInit = {}) => {
  const r = await fetch(url, { ...init, headers: { ...auth().headers, ...(init.headers || {}) } });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
};

const DAYS: { key: 'mon'|'tue'|'wed'|'thu'|'fri'|'sat'|'sun'; label: string }[] = [
  { key: 'mon', label: 'Monday' },
  { key: 'tue', label: 'Tuesday' },
  { key: 'wed', label: 'Wednesday' },
  { key: 'thu', label: 'Thursday' },
  { key: 'fri', label: 'Friday' },
  { key: 'sat', label: 'Saturday' },
  { key: 'sun', label: 'Sunday' },
];

// Curated 12-entry timezone list. Full IANA tz database is 600+ — this covers
// the US/Americas common cases. Extend as needed.
const TIMEZONES = [
  { v: 'America/New_York',     label: '-5:00 United States — Eastern Time' },
  { v: 'America/Chicago',      label: '-6:00 United States — Central Time' },
  { v: 'America/Denver',       label: '-7:00 United States — Mountain Time' },
  { v: 'America/Phoenix',      label: '-7:00 United States — Arizona' },
  { v: 'America/Los_Angeles',  label: '-8:00 United States — Pacific Time' },
  { v: 'America/Anchorage',    label: '-9:00 United States — Alaska' },
  { v: 'Pacific/Honolulu',     label: '-10:00 United States — Hawaii' },
  { v: 'America/Toronto',      label: '-5:00 Canada — Eastern' },
  { v: 'America/Vancouver',    label: '-8:00 Canada — Pacific' },
  { v: 'Europe/London',        label: '+0:00 United Kingdom' },
  { v: 'Europe/Paris',         label: '+1:00 Central Europe' },
  { v: 'UTC',                  label: '+0:00 UTC' },
];

type Range = { start: string; end: string };
type DayRanges = Record<string, Range[]>;
type Holiday = {
  id?: string;
  name: string;
  kind: 'single' | 'range'; // Single day / Date range
  start_date: string;
  end_date?: string;
  time_start?: string; // 24h "HH:MM"
  time_end?: string;
  whole_day: boolean;
  annually: boolean;
  prompt_audio_id?: string;
  // Back-compat: legacy rows used `occurs: 'once' | 'yearly'`
  occurs?: 'once' | 'yearly';
};

export default function OfficeHours() {
  const [params, setParams] = useSearchParams();
  const qc = useQueryClient();
  const [tab, setTab] = useState<'hours' | 'holidays'>('hours');

  // ── Left rail: list of tenants/departments ──────────────────────────────
  const tenantsQ = useQuery<any[]>({
    queryKey: ['tenants'],
    queryFn: () => fJson('/api/tenants'),
  });
  // Skip nameless / empty entries — those render as a blank blue bar in the
  // rail when auto-selected. A real tenant must have at least a domain_uuid
  // AND a description or name to display.
  const tenants = (tenantsQ.data || [])
    .filter((t: any) => t?.domain_uuid && (t.domain_description?.trim() || t.domain_name?.trim()))
    .filter((t: any) => matchesScope(t.domain_uuid))   // global dept filter: rail shows only the selected business
    .slice()
    .sort((a: any, b: any) =>
      (a.domain_description || a.domain_name || '').localeCompare(b.domain_description || b.domain_name || ''));

  const _scope = getActiveTenant();
  const selectedDomain = (_scope !== ALL ? _scope : params.get('domain_uuid')) || tenants[0]?.domain_uuid || '';
  function selectDept(d: string) {
    const next = new URLSearchParams(params);
    next.set('domain_uuid', d);
    setParams(next, { replace: true });
  }
  // Auto-select first tenant once loaded
  useEffect(() => {
    if (_scope === ALL && !params.get('domain_uuid') && tenants.length > 0) {
      selectDept(tenants[0].domain_uuid);
    }
  }, [tenants.length]);

  // ── Right pane data: tenant config (timezone + hours + breaks + holidays) ──
  const { data: tenant } = useQuery<any>({
    queryKey: ['tenant-detail', selectedDomain],
    queryFn: () => fJson(`/api/tenants/${selectedDomain}`),
    enabled: !!selectedDomain,
  });

  return (
    <div className="space-y-3">
      <h1 className="text-2xl font-semibold">Office Hours</h1>

      <div className="flex gap-4 min-h-[600px]">
        {/* Left rail — alphabetical department list */}
        <div className="w-56 flex-shrink-0 space-y-1 max-h-[80vh] overflow-y-auto">
          {tenants.map((t: any) => (
            <button
              key={t.domain_uuid}
              onClick={() => selectDept(t.domain_uuid)}
              className={`w-full text-left px-3 py-2 rounded-md text-sm ${
                selectedDomain === t.domain_uuid ? 'bg-primary text-white' : 'hover:bg-elevated'
              }`}
            >
              {t.domain_description || t.domain_name}
            </button>
          ))}
        </div>

        {/* Right pane */}
        <div className="flex-1 min-w-0 card p-4 space-y-4">
          {/* Tabs */}
          <div className="flex gap-1 border-b border-border -mt-1">
            <button
              onClick={() => setTab('hours')}
              className={`px-4 py-2 text-sm ${tab === 'hours' ? 'border-b-2 border-primary text-primary' : 'text-textSecondary'}`}
            >Office Hours</button>
            <button
              onClick={() => setTab('holidays')}
              className={`px-4 py-2 text-sm ${tab === 'holidays' ? 'border-b-2 border-primary text-primary' : 'text-textSecondary'}`}
            >Office Holidays</button>
          </div>

          {tab === 'hours' && tenant && (
            <HoursTab tenant={tenant} qc={qc} />
          )}
          {tab === 'holidays' && tenant && (
            <HolidaysTab tenant={tenant} qc={qc} />
          )}
          {!tenant && <div className="text-textSecondary text-sm">Select a department to configure.</div>}
        </div>
      </div>
    </div>
  );
}

// ── Office Hours tab — timezone + Office grid + Break grid ─────────────────
function HoursTab({ tenant, qc }: any) {
  const oh = tenant?.office_hours || {};
  const [tz, setTz] = useState<string>(oh.timezone || 'America/New_York');
  const [hours, setHours] = useState<DayRanges>(() => normalizeRanges(oh.hours, true));
  const [breaks, setBreaks] = useState<DayRanges>(() => normalizeRanges(oh.breaks, false));

  // Re-init when tenant changes
  useEffect(() => {
    setTz(oh.timezone || 'America/New_York');
    setHours(normalizeRanges(oh.hours, true));
    setBreaks(normalizeRanges(oh.breaks, false));
  }, [tenant?.domain_uuid]);

  const save = useMutation({
    mutationFn: () => fJson(`/api/tenants/${tenant.domain_uuid}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...tenant, office_hours: { ...oh, timezone: tz, hours, breaks } }),
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tenant-detail', tenant.domain_uuid] }),
  });

  return (
    <div className="space-y-5">
      <div>
        <h2 className="font-semibold">Set your time zone</h2>
        <select className="input mt-2" value={tz} onChange={e => setTz(e.target.value)}>
          {TIMEZONES.map(t => <option key={t.v} value={t.v}>{t.label}</option>)}
        </select>
      </div>

      <div>
        <h2 className="font-semibold">Set your office and break times</h2>
      </div>

      {/* Office Hours grid */}
      <RangeGrid
        title="Office Hours"
        ranges={hours}
        onChange={setHours}
      />

      {/* Break Hours grid */}
      <RangeGrid
        title="Break Hours"
        ranges={breaks}
        onChange={setBreaks}
      />

      <div className="flex justify-end">
        <button className="btn-primary inline-flex items-center gap-1" disabled={save.isPending} onClick={() => save.mutate()}>
          <Save size={14} /> {save.isPending ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

function normalizeRanges(input: any, defaultWorkdays: boolean): DayRanges {
  const out: DayRanges = {};
  for (const d of DAYS) {
    if (Array.isArray(input?.[d.key])) {
      out[d.key] = input[d.key];
    } else if (input?.[d.key]?.start) {
      // Old single-range shape — migrate
      out[d.key] = [{ start: input[d.key].start, end: input[d.key].end }];
    } else if (defaultWorkdays && ['mon','tue','wed','thu','fri'].includes(d.key)) {
      out[d.key] = [{ start: '09:00', end: '17:00' }];
    } else {
      out[d.key] = [];
    }
  }
  return out;
}

// 7-column day grid with multiple time ranges per day + per-day Add link.
function RangeGrid({ title, ranges, onChange }: { title: string; ranges: DayRanges; onChange: (r: DayRanges) => void }) {
  function addRange(day: string) {
    onChange({ ...ranges, [day]: [...(ranges[day] || []), { start: '09:00', end: '17:00' }] });
  }
  function clearAll() {
    onChange(Object.fromEntries(DAYS.map(d => [d.key, []])) as DayRanges);
  }
  function updateRange(day: string, idx: number, patch: Partial<Range>) {
    const next = (ranges[day] || []).slice();
    next[idx] = { ...next[idx], ...patch };
    onChange({ ...ranges, [day]: next });
  }
  function deleteRange(day: string, idx: number) {
    const next = (ranges[day] || []).slice();
    next.splice(idx, 1);
    onChange({ ...ranges, [day]: next });
  }

  return (
    <div className="border-t border-border pt-3">
      <div className="flex items-center justify-between mb-2">
        <div className="font-medium">{title}</div>
        <div className="space-x-3 text-sm">
          <button className="text-primary hover:underline" onClick={() => DAYS.forEach(d => addRange(d.key))}>Add</button>
          <button className="text-primary hover:underline" onClick={clearAll}>Clear</button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-2 text-xs">
        {DAYS.map(d => (
          <div key={d.key} className="space-y-1.5">
            <div className="font-medium">{d.label}</div>
            {(ranges[d.key] || []).map((r, idx) => (
              <div key={idx} className="space-y-0.5">
                <div className="flex items-center gap-1">
                  <input
                    type="time"
                    className="input p-1 text-xs flex-1 min-w-0"
                    value={r.start}
                    onChange={e => updateRange(d.key, idx, { start: e.target.value })}
                  />
                  <button onClick={() => deleteRange(d.key, idx)} className="text-textSecondary hover:text-danger" title="Delete range">
                    <X size={10} />
                  </button>
                </div>
                <input
                  type="time"
                  className="input p-1 text-xs w-full"
                  value={r.end}
                  onChange={e => updateRange(d.key, idx, { end: e.target.value })}
                />
              </div>
            ))}
            <button onClick={() => addRange(d.key)} className="text-primary hover:underline text-xs">Add</button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Office Holidays tab — table + add modal ────────────────────────────────
function HolidaysTab({ tenant, qc }: any) {
  const [showForm, setShowForm] = useState<Holiday | null>(null);
  const oh = tenant?.office_hours || {};
  const holidays: Holiday[] = oh.holidays || [];

  const save = useMutation({
    mutationFn: (next: Holiday[]) => fJson(`/api/tenants/${tenant.domain_uuid}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...tenant, office_hours: { ...oh, holidays: next } }),
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tenant-detail', tenant.domain_uuid] }),
  });

  function addOrUpdate(h: Holiday) {
    let next: Holiday[];
    if (h.id) {
      next = holidays.map(x => x.id === h.id ? h : x);
    } else {
      next = [...holidays, { ...h, id: crypto.randomUUID() }];
    }
    save.mutate(next);
    setShowForm(null);
  }
  function remove(id?: string) {
    if (!id) return;
    if (!confirm('Delete this holiday?')) return;
    save.mutate(holidays.filter(h => h.id !== id));
  }

  return (
    <div className="space-y-3">
      <button
        className="btn-primary inline-flex items-center gap-1"
        onClick={() => setShowForm({
          name: '', kind: 'single', start_date: todayIso(),
          time_start: '00:00', time_end: '00:00',
          whole_day: true, annually: false,
        })}
      >
        <Plus size={14} /> Add
      </button>
      <table className="table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Date</th>
            <th>Time</th>
            <th>Occurs</th>
            <th>Prompt</th>
            <th className="w-16"></th>
          </tr>
        </thead>
        <tbody>
          {holidays.map(h => {
            // Migrate legacy `occurs` to `annually` on display.
            const annually = h.annually ?? (h.occurs === 'yearly');
            return (
              <tr key={h.id} className="hover:bg-elevated">
                <td className="font-medium">{h.name}</td>
                <td>{formatDateRange(h.start_date, h.end_date)}</td>
                <td>{h.whole_day ? '' : (h.time_start ? `${h.time_start} – ${h.time_end || '24:00'}` : '')}</td>
                <td>{annually ? 'Annually' : 'Once'}</td>
                <td className="text-xs">{h.prompt_audio_id || ''}</td>
                <td className="space-x-1">
                  <button className="btn-ghost p-1" onClick={() => setShowForm(h)} title="Edit"><Edit2 size={12} /></button>
                  <button className="btn-ghost p-1 text-danger" onClick={() => remove(h.id)} title="Delete"><Trash2 size={12} /></button>
                </td>
              </tr>
            );
          })}
          {holidays.length === 0 && (
            <tr><td colSpan={6} className="text-center py-6 text-textSecondary text-sm">No holidays yet — click <strong>+ Add</strong> to create one.</td></tr>
          )}
        </tbody>
      </table>

      {showForm && (
        <HolidayForm value={showForm} onCancel={() => setShowForm(null)} onSave={addOrUpdate} />
      )}
    </div>
  );
}

// Configure Holidays modal — matches 3CX shape:
//   • Title: "Configure Holidays" + descriptive subtitle
//   • Name (required, full-width)
//   • [Single day | Date range] dropdown + Annually checkbox on same row
//   • Date(s) input + HH:MM AM/PM time pickers + "Whole day" toggle button
//   • Prompt section: audio file dropdown + 4 icon buttons (upload / delete / record / library)
//   • Save / Cancel
function HolidayForm({ value, onCancel, onSave }: { value: Holiday; onCancel: () => void; onSave: (h: Holiday) => void }) {
  const [form, setForm] = useState<Holiday>(() => ({
    ...value,
    // Back-compat normalization for legacy rows
    kind: value.kind || (value.end_date && value.end_date !== value.start_date ? 'range' : 'single'),
    annually: value.annually ?? (value.occurs === 'yearly'),
    whole_day: value.whole_day ?? (!value.time_start || (value.time_start === '00:00' && (!value.time_end || value.time_end === '00:00'))),
    time_start: value.time_start || '00:00',
    time_end: value.time_end || '00:00',
  }));
  const set = (k: keyof Holiday, v: any) => setForm({ ...form, [k]: v });

  // Time picker helpers — 12-hour HH:MM AM/PM mapped to 24-hour HH:MM string
  const startParts = parseTime(form.time_start || '00:00');
  const endParts   = parseTime(form.time_end   || '00:00');
  function setStart(p: TimeParts) { set('time_start', formatTime(p)); }
  function setEnd(p: TimeParts)   { set('time_end',   formatTime(p)); }

  function toggleWholeDay() {
    if (form.whole_day) {
      // turning off — leave the values as-is
      set('whole_day', false);
    } else {
      // turning on — normalize to 12:00 AM start, 12:00 AM end (3CX shape)
      setForm({ ...form, whole_day: true, time_start: '00:00', time_end: '00:00' });
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={onCancel}>
      <div className="card max-w-2xl w-full p-6 space-y-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold">Configure Holidays</h2>
          <button onClick={onCancel} className="btn-ghost p-1"><X size={16} /></button>
        </div>

        <p className="text-sm text-textSecondary">
          In addition to office hours and days, define specific dates as public holidays. Holidays default to out of office hours.
        </p>

        <div>
          <label className="text-sm font-medium">Name <span className="text-danger">*</span></label>
          <input className="input mt-1" value={form.name} onChange={e => set('name', e.target.value)} />
        </div>

        <div className="flex items-center gap-4">
          <select className="input max-w-[180px]" value={form.kind} onChange={e => set('kind', e.target.value as any)}>
            <option value="single">Single day</option>
            <option value="range">Date range</option>
          </select>
          <label className="text-sm flex items-center gap-2">
            <input type="checkbox" checked={!!form.annually} onChange={e => set('annually', e.target.checked)} />
            Annually
          </label>
        </div>

        {/* Date(s) + time pickers + Whole day */}
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="date"
            className="input max-w-[160px]"
            value={form.start_date}
            onChange={e => set('start_date', e.target.value)}
          />
          {form.kind === 'range' && (
            <>
              <span className="text-textSecondary">→</span>
              <input
                type="date"
                className="input max-w-[160px]"
                value={form.end_date || ''}
                onChange={e => set('end_date', e.target.value)}
              />
            </>
          )}

          <TimeBlock
            parts={startParts}
            onChange={setStart}
            disabled={!!form.whole_day}
          />
          <span className="text-textSecondary">—</span>
          <TimeBlock
            parts={endParts}
            onChange={setEnd}
            disabled={!!form.whole_day}
          />

          <button
            onClick={toggleWholeDay}
            className={`btn ${form.whole_day ? 'bg-primary text-white border-primary' : ''}`}
          >Whole day</button>
        </div>

        {/* Prompt section */}
        <div>
          <label className="text-sm font-medium">Prompt</label>
          <AudioPicker value={form.prompt_audio_id || ''} onChange={v => set('prompt_audio_id', v)} placeholder="— No prompt —" />
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t border-border">
          <button
            onClick={() => onSave(form)}
            disabled={!form.name || !form.start_date}
            className="btn-primary"
          >Save</button>
          <button onClick={onCancel} className="btn">Cancel</button>
        </div>
      </div>
    </div>
  );
}

// 12-hour time helpers
type TimeParts = { hh: number; mm: number; ampm: 'AM' | 'PM' };
function parseTime(t: string): TimeParts {
  const [hStr, mStr] = (t || '00:00').split(':');
  const h24 = parseInt(hStr, 10) || 0;
  const mm = parseInt(mStr, 10) || 0;
  const ampm: 'AM' | 'PM' = h24 >= 12 ? 'PM' : 'AM';
  let hh = h24 % 12;
  if (hh === 0) hh = 12;
  return { hh, mm, ampm };
}
function formatTime(p: TimeParts): string {
  let h = p.hh % 12;
  if (p.ampm === 'PM') h += 12;
  return `${String(h).padStart(2, '0')}:${String(p.mm).padStart(2, '0')}`;
}

function TimeBlock({ parts, onChange, disabled }: { parts: TimeParts; onChange: (p: TimeParts) => void; disabled?: boolean }) {
  return (
    <div className="inline-flex items-center gap-1">
      <input
        type="number" min={1} max={12} disabled={disabled}
        className="input w-14 text-center font-mono disabled:opacity-50"
        value={parts.hh}
        onChange={e => onChange({ ...parts, hh: clamp(parseInt(e.target.value, 10) || 1, 1, 12) })}
      />
      <span className="text-textSecondary">:</span>
      <input
        type="number" min={0} max={59} disabled={disabled}
        className="input w-14 text-center font-mono disabled:opacity-50"
        value={String(parts.mm).padStart(2, '0')}
        onChange={e => onChange({ ...parts, mm: clamp(parseInt(e.target.value, 10) || 0, 0, 59) })}
      />
      <select
        className="input max-w-[64px] disabled:opacity-50" disabled={disabled}
        value={parts.ampm}
        onChange={e => onChange({ ...parts, ampm: e.target.value as 'AM' | 'PM' })}
      >
        <option>AM</option>
        <option>PM</option>
      </select>
    </div>
  );
}

function clamp(n: number, lo: number, hi: number) { return Math.max(lo, Math.min(hi, n)); }
function todayIso() { return new Date().toISOString().slice(0, 10); }

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <label className="text-sm font-medium">{label}</label>
      {children}
    </div>
  );
}

function formatDateRange(start: string, end?: string): string {
  if (!start) return '';
  const fmt = (s: string) => {
    const d = new Date(s + 'T00:00:00');
    return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };
  return end && end !== start ? `${fmt(start)} - ${fmt(end)}` : fmt(start);
}
